import { useState } from "react";
import { getSelectWithId, getSelectWithNumber } from "common/query/select";
import { addFilterToQuery } from "common/query/filter";
import { EntityAction } from "common/app/command-palette/providers/entities/types";
import {
  getActions,
  menuEntryForEntity,
} from "common/app/command-palette/providers/entities/functions";
import { getCommonSelectorProps } from "common/app/command-palette/selectors";
import { formatCommand } from "common/app/command-palette/list-item";
import { FormSelector } from "common/form/form-selector";
import { Selector } from "common/widgets/selector";
import { searchApi } from "common/api/search";
import { RenderModuleProps } from "common/app/command-palette/types";
import { Entity } from "common/entities/types";
import { getFkId } from "common/functions/foreign-key";
import { QueryForEntity } from "common/query/types";
import { FkValue } from "common/types/foreign-key";
import { RecordSelector } from "common/widgets/record-selector/selector";
import { ReportSelector } from "common/reporting/selector";
import { Report } from "common/types/reports";

interface EntityModuleProps extends RenderModuleProps {
  entity: Entity;
}

export function EntityModule({
  context,
  goTo,
  onCommandSelected,
  entity,
}: Readonly<EntityModuleProps>) {
  const [step, setStep] = useState<string>("entity");

  const onActionSelected = (action: EntityAction) => {
    if (action.id === "list") {
      const link =
        entity.type === "Reference"
          ? `#/${context.site.name}/admin/References/${entity.name}`
          : `#/${context.site.name}/${entity.name}`;

      goTo(link);
      onCommandSelected();
    } else if (action.id === "last-viewed") {
      const menuEntry = menuEntryForEntity(context, entity);

      if (menuEntry.lastViewedLink) goTo(menuEntry.lastViewedLink);

      onCommandSelected();
    } else if (action.id === "settings") {
      goTo(`#/${context.site.name}/admin/Entities/${entity.name}`);
      onCommandSelected();
    } else if (action.id === "forms") {
      setStep("forms");
    } else if (action.id === "reports") {
      setStep("reports");
    } else if (action.id === "records") {
      setStep("records");
    } else {
      onCommandSelected();
    }
  };

  const onFormSelected = (formId: number) => {
    goTo(`#/${context.site.name}/admin/Forms/${formId}`);
    onCommandSelected();
  };

  const onReportSelected = (report: Report) => {
    goTo(`#/${context.site.name}/Reports/${report.id}`);
    onCommandSelected();
  };

  const onRecordSelected = (id: FkValue) => {
    goTo(`#/${context.site.name}/${entity.name}/${getFkId(id)}`);
    onCommandSelected();
  };

  const runQuery = (queryForEntity: QueryForEntity) => {
    const { entity, query } = queryForEntity;
    const { select, group } = query;

    const finalFilter = addFilterToQuery(
      {
        or: [
          { name: "isDeleted", op: "istrue" },
          { name: "isDeleted", op: "isfalse" },
        ],
      },
      query,
    ).filter;
    const finalSelect = getSelectWithNumber(getSelectWithId(select));
    const finalGroup = group
      ? (group ?? []).concat([{ name: "number" }])
      : undefined;

    const finalQuery = {
      entity,
      query: {
        ...query,
        select: finalSelect,
        group: finalGroup,
        filter: finalFilter,
      },
    };
    return searchApi(context.apiCall).runQueryForLookup(finalQuery);
  };

  if (step === "forms") {
    return (
      <FormSelector
        {...getCommonSelectorProps()}
        forms={context.forms.filter((f) => f.entityName === entity.name)}
        value={undefined}
        onChange={onFormSelected}
      />
    );
  }

  if (step === "reports") {
    return (
      <ReportSelector
        {...getCommonSelectorProps()}
        placeholder={_("Select report")}
        openAfterFetch={true}
        context={context}
        entity={entity.name}
        value={undefined}
        onChange={onReportSelected}
      />
    );
  }

  if (step === "records") {
    return (
      <RecordSelector
        {...getCommonSelectorProps()}
        placeholder={_("Select record")}
        context={context}
        entity={entity}
        runQuery={runQuery}
        showDeleted={true}
        useIdMatch={true}
        value={undefined}
        onChange={onRecordSelected}
      />
    );
  }

  return (
    <Selector
      {...getCommonSelectorProps()}
      placeholder={_("Select action for {ENTITY}").replace(
        "{ENTITY}",
        entity.localizedName,
      )}
      options={getActions(context, entity)}
      getOptionLabel={(o) => o.label}
      formatOption={formatCommand}
      disableSorting={true}
      value={undefined}
      onChange={onActionSelected}
    />
  );
}

EntityModule.displayName = "EntityModule";
